import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import '../styles/tipoSensor.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';
import { useNavigate } from 'react-router-dom';

const TipoSensorManager = () => {
    const navigate = useNavigate();
    const [dados, setDados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
    const [formData, setFormData] = useState({
        id_tipo_sensor: '',
        descricao: ''
    });
    const [apiStatus, setApiStatus] = useState('checking');
    const [lastUpdate, setLastUpdate] = useState(null);

    const tabulatorRef = useRef(null);
    const tableRef = useRef(null);

    const mostrarMensagem = useCallback((tipo, texto) => {
        setMensagem({ tipo, texto });
        setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
    }, []);

    const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
        const dadosParaEnviar = { ...dadosAtualizacao };
        delete dadosParaEnviar.salvar;
        delete dadosParaEnviar.excluir;

        console.log('📤 Atualizando tipo de sensor:', { id, dadosParaEnviar });

        try {
            const response = await api.put(`/tipo-sensor/${id}`, dadosParaEnviar);
            console.log('✅ Tipo de sensor atualizado:', response.data);
            if (response && response.data) {
                if (tabulatorRef.current) {
                    tabulatorRef.current.updateData([response.data.data]);
                }
                mostrarMensagem('success', 'Registro atualizado com sucesso!');
            } else {
                mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
            }
        } catch (error) {
            console.error('❌ Erro ao atualizar tipo de sensor:', error);
            mostrarMensagem('error', `Erro ao atualizar: ${error.message}`);
        }
    }, [mostrarMensagem]);

    const excluirRegistro = useCallback(async (id) => {
        console.log('📤 Excluindo tipo de sensor:', id);
        try {
            await api.delete(`/tipo-sensor/${id}`);
            console.log('✅ Tipo de sensor excluído:', id);
            setDados(currentData => currentData.filter(item => item.id_tipo_sensor !== id));
            mostrarMensagem('success', 'Registro excluído com sucesso!');
        } catch (error) {
            console.error('❌ Erro ao excluir tipo de sensor:', error);
            mostrarMensagem('error', `Erro ao excluir: ${error.message}`);
        }
    }, [mostrarMensagem]);

    const getColunas = useMemo(() => {
        return [
            {
                title: 'Salvar',
                field: 'salvar',
                width: 80,
                hozAlign: 'center',
                formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
                cellClick: (e, cell) => {
                    const data = cell.getRow().getData();
                    atualizarRegistro(data.id_tipo_sensor, data);
                },
                headerSort: false,
            },
            {
                title: 'Excluir',
                field: 'excluir',
                width: 80,
                hozAlign: 'center',
                formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
                cellClick: (e, cell) => {
                    const data = cell.getRow().getData();
                    if (window.confirm(`Deseja realmente excluir o tipo de sensor "${data.descricao}"?`)) {
                        excluirRegistro(data.id_tipo_sensor);
                    }
                },
                headerSort: false,
            },
            {
                title: 'ID Tipo Sensor',
                field: 'id_tipo_sensor',
                width: 300,
                headerFilter: 'input',
                headerFilterPlaceholder: 'Filtrar ID...',
                sorter: 'string',
                frozen: true,
                visible: false,
            },
            {
                title: 'Descrição',
                field: 'descricao',
                minWidth: 300,
                headerFilter: 'input',
                headerFilterPlaceholder: 'Filtrar descrição...',
                sorter: 'string',
                editor: 'input'
            }
        ];
    }, [atualizarRegistro, excluirRegistro]);

    const inicializarTabulator = useCallback(() => {
        if (!tableRef.current) return;
        if (tabulatorRef.current) tabulatorRef.current.destroy();

        tabulatorRef.current = new Tabulator(tableRef.current, {
            data: dados,
            columns: getColunas,
            layout: 'fitColumns',
            pagination: 'local',
            paginationSize: 10,
            paginationCounter: 'rows',
            movableColumns: true,
            resizableRows: true,
            resizableColumns: true,
            tooltips: true,
            placeholder: 'Nenhum registro encontrado',
            index: 'id_tipo_sensor'
        });

        const style = document.createElement('style');
        style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
    `;
        document.head.appendChild(style);

    }, [dados, getColunas]);

    const carregarDados = useCallback(async (showMessage = true) => {
        try {
            setLoading(true);
            const response = await api.get('/tipo-sensor');
            setDados(response.data.data || []);
            setLastUpdate(new Date());
            if (showMessage) {
                mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
            }
        } catch (error) {
            mostrarMensagem('error', error.message);
            setDados([]);
        } finally {
            setLoading(false);
        }
    }, [mostrarMensagem]);

    const verificarAPI = useCallback(async () => {
        try {
            setApiStatus('checking');
            await api.get('/tipo-sensor');
            setApiStatus('connected');
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setApiStatus('error');
                mostrarMensagem('error', `Erro de conexão: ${error.message}`);
            } else {
                setApiStatus('connected');
            }
        }
    }, [mostrarMensagem]);

    useEffect(() => {
        verificarAPI();
    }, [verificarAPI]);

    useEffect(() => {
        if (apiStatus === 'connected') {
            carregarDados(false);
        }
    }, [apiStatus, carregarDados]);

    useEffect(() => {
        if (apiStatus === 'connected' && tableRef.current) {
            inicializarTabulator();
        }
    }, [dados, getColunas, apiStatus, inicializarTabulator]);

    const adicionarRegistro = async (e) => {
        e.preventDefault();
        const { descricao } = formData;

        if (!descricao) {
            mostrarMensagem('error', 'Por favor, preencha a descrição.');
            return;
        }

        const dataToSend = {
            descricao
        };

        try {
            setLoading(true);
            const response = await api.post('/tipo-sensor', dataToSend);
            console.log('✅ Tipo de sensor adicionado:', response.data);
            setDados([...dados, response.data.data]);
            setFormData({ id_tipo_sensor: '', descricao: '' });
            mostrarMensagem('success', 'Registro adicionado com sucesso!');
        } catch (error) {
            console.error('❌ Erro ao adicionar tipo de sensor:', error);
            mostrarMensagem('error', `Erro ao adicionar: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const exportarDados = () => {
        if (tabulatorRef.current) {
            tabulatorRef.current.download('csv', 'tipos_dispositivos_iot.csv');
        }
    };

    return (
        <div className="tipo-sensor-container">
            <div className="header-controls">
                <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
            </div>

            <div className="header">
                <div className="title-container">
                    <img src={etgLogo} alt="ETG Logo" className="logo" />
                    <h1>Gerenciador de Tipos de Dispositivo Iot</h1>
                    <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
                </div>
                <p>Interface para popular e gerenciar a tabela tb_tipo_sensor</p>
            </div>

            {mensagem.texto && (
                <div className="status-container">
                    <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
                </div>
            )}

            <div className="controls">
                <form onSubmit={adicionarRegistro} className="form-inline">
                    <div className="form-group">
                        <label htmlFor="descricao">Descrição:<span style={{ color: 'red', marginLeft: '5px' }}>*</span></label>
                        <input
                            type="text"
                            id="descricao"
                            name="descricao"
                            value={formData.descricao}
                            onChange={handleInputChange}
                            placeholder="Descrição do tipo de sensor"
                            maxLength={255}
                            disabled={loading}
                            required
                        />
                    </div>

                    <div className="form-actions-row">
                        <div className="form-buttons">
                            <button type="submit" className="btn btn-success" disabled={loading}>
                                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
                            </button>
                            <button type="button" className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading}>
                                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>
                                📥 Exportar CSV
                            </button>
                        </div>

                        <div className="info-campos-obrigatorios">
                            <i className="fas fa-info-circle"></i>
                            <span>* indica que os campos são obrigatorios.</span>
                        </div>
                    </div>
                </form>
            </div>

            <div className="stats-bar">
                <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
                <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
            </div>

            <div className="tabulator-container">
                <div ref={tableRef}></div>
            </div>
        </div>
    );
};

export default TipoSensorManager;
